import { forwardRef, Ref, useMemo } from "react";
import cx from "classnames";

import { SelectMenuSync } from "../../../../../../beta/components/SelectMenu/SelectMenuSync";
import { SelectMenuOption } from "../../../../../../beta/components/SelectMenu/types";

import { SrOnly } from "../../../../../../components/SrOnly";

import { DataTableEditableCellEditIcon } from "./DataTableEditableCellEditIcon";
import { TableBodyCell } from "../../../base/cells/TableBodyCell";

import styles from "./DataTableEditableCells.module.scss";
import { EditableCellImperativeHandle } from "./types";
import { formatCellPositionString } from "../util/cellPositionHelpers";
import { mergeRefs } from "../../../../../../hooks/useMergeRefs/useMergeRefs";
import { BaseEditableCellProps } from "./types";
import {
  getColumnId,
  getCellValue,
  logWarningForInvalidCellValueType,
} from "../util/cellTypeHelpers";
import { DataTableBodyImmutableCell } from "../cells/DataTableBodyImmutableCell";
import type { BooleanFormatterOptions } from "../../../formatters";
import { CellFocusContext } from "../cells/CellFocusContext";
import { useEditableMenuCell } from "./useEditableMenuCell";

export type DataTableEditableBooleanCellProps<T> = BaseEditableCellProps<T>;

/**
 * Renders an editable boolean cell in a DataTable. This component provides a dropdown menu
 * with predefined true/false options (and an optional null option) that appears when the cell
 * is activated. Labels for each option are configurable via editConfig.
 *
 * @param props - The props for the DataTableEditableBooleanCell component
 * @param props.cell - The TanStack table cell instance
 * @param props.cellProps - Props for styling and event handling of the cell container
 * @param props.cellPosition - The position of the cell in the table
 * @param props.children - The content to display when not editing
 * @param props.isHovered - Whether the cell is currently hovered
 * @param ref - Ref to the imperative handle for programmatic control
 * @returns The rendered editable boolean cell component
 */
function DataTableEditableBooleanCellInner<T>(
  props: DataTableEditableBooleanCellProps<T>,
  ref: React.Ref<EditableCellImperativeHandle>,
) {
  const { cell, cellProps, cellPosition, children } = props;
  const { onKeyDown: onCellKeyDown, className, ...restCellProps } = cellProps;

  const columnId = getColumnId(cell);
  const cellValue = getCellValue(cell, columnId);

  const meta = cell.column.columnDef.meta;
  const editConfig = meta?.editConfig;
  const booleanEditConfig =
    editConfig?.mode === "boolean" ? editConfig : undefined;

  const [, isValidValueType] = useMemo(() => {
    let value: boolean | null;
    let isValid = true;

    if (cellValue === undefined || cellValue === null) {
      value = null;
      isValid = true;
      return [value, isValid];
    }

    if (typeof cellValue === "boolean") {
      value = cellValue;
    } else {
      value = null;
      isValid = false;
    }

    if (!isValid) {
      logWarningForInvalidCellValueType({
        columnId: String(columnId),
        editMode: "boolean",
        expectedType: "boolean",
        receivedType: Array.isArray(cellValue) ? "array" : typeof cellValue,
      });
    }
    return [value, isValid];
  }, [cellValue, columnId]);

  const options: SelectMenuOption[] = useMemo(() => {
    const config =
      booleanEditConfig?.mode === "boolean"
        ? booleanEditConfig
        : {
            trueLabel: undefined,
            falseLabel: undefined,
            allowNull: false,
            nullLabel: undefined,
          };

    // Fall back to type.options labels when editConfig doesn't specify them
    const typeConfig = cell.column.columnDef.meta?.columnDef?.type;
    const typeOptions: BooleanFormatterOptions | undefined =
      typeof typeConfig === "object" && typeConfig.type === "boolean"
        ? (typeConfig.options as BooleanFormatterOptions)
        : undefined;

    const items: SelectMenuOption[] = [
      {
        id: "true",
        label: config.trueLabel ?? typeOptions?.trueLabel ?? "True",
      },
      {
        id: "false",
        label: config.falseLabel ?? typeOptions?.falseLabel ?? "False",
      },
    ];
    if (config.allowNull) {
      items.push({ id: "null", label: config.nullLabel ?? "Unset" });
    }
    return items;
  }, [booleanEditConfig, cell.column.columnDef.meta?.columnDef?.type]);

  const rowId = cell.row.id;
  const onChange = booleanEditConfig?.onChange;
  const hasEditableConfig = !!booleanEditConfig;

  const {
    tabIndex,
    cellRef,
    triggerClickRef,
    handleCellKeyDown,
    handleMenuKeyDown,
    handleImplicitClose,
    handleCellMouseDown,
    handleCellBlur,
    handleCellFocus,
    cellFocusContextValue,
  } = useEditableMenuCell({ cellPosition, onCellKeyDown, ref });

  // Find selected option by matching cell value to option id
  const selectedOption = useMemo(() => {
    if (cellValue === undefined) return null;
    return options.find((o) => o.id === String(cellValue)) ?? null;
  }, [options, cellValue]);

  if (!isValidValueType || !hasEditableConfig) {
    const { cellProps: _cellProps, ...immutableCellProps } = props;
    return <DataTableBodyImmutableCell {...immutableCellProps} />;
  }

  return (
    <SelectMenuSync
      trigger={({
        ref: triggerRef,
        onClick,
        onKeyDown: menuKeyDown,
        "aria-controls": ariaControls,
        "aria-expanded": ariaExpanded,
        "aria-haspopup": ariaHaspopup,
        "data-state": dataState,
      }) => {
        triggerClickRef.current = onClick;
        const isOpen = ariaExpanded;
        return (
          <TableBodyCell
            ref={mergeRefs([
              triggerRef as Ref<HTMLElement>,
              cellRef as Ref<HTMLElement>,
            ])}
            tabIndex={tabIndex}
            type="data-table"
            {...restCellProps}
            className={cx(styles["data-table-body-cell-editable"], className)}
            onMouseDown={handleCellMouseDown(onClick)}
            onBlur={handleCellBlur}
            onFocus={handleCellFocus}
            data-cell-pos={formatCellPositionString(cellPosition)}
            aria-controls={ariaControls}
            aria-expanded={ariaExpanded}
            aria-haspopup={ariaHaspopup}
            data-state={dataState}
            onKeyDown={(e) => handleCellKeyDown(e, menuKeyDown, isOpen)}
          >
            <CellFocusContext.Provider value={cellFocusContextValue}>
              {children}
              {!isOpen && <DataTableEditableCellEditIcon />}
            </CellFocusContext.Provider>
            <SrOnly>
              {isOpen
                ? "Press Enter or Space to select a cell value. Press Escape or F2 to cancel."
                : "Press Enter or F2 to edit cell value."}
            </SrOnly>
          </TableBodyCell>
        );
      }}
      label={String(
        cell.column.columnDef.meta?.columnDef?.headerLabel ?? "Select",
      )}
      options={options}
      value={selectedOption}
      onSelectedOptionChange={(option) => {
        if (!option) return;
        const id = String(option.id);
        const value = id === "true" ? true : id === "false" ? false : null;
        onChange?.(value as T[typeof columnId], rowId);
      }}
      disableSearch
      onMenuKeyDown={handleMenuKeyDown}
      onImplicitClose={handleImplicitClose}
      popoverWidth="reference"
    />
  );
}

export const DataTableEditableBooleanCell = Object.assign(
  forwardRef(DataTableEditableBooleanCellInner) as <T>(
    props: DataTableEditableBooleanCellProps<T> & {
      ref?: React.Ref<EditableCellImperativeHandle>;
    },
  ) => React.ReactElement,
  { displayName: "DataTableEditableBooleanCell" },
);
